"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useFeatureFlag, usePostHog } from "@posthog/next";
import { routeGailAppUrl } from "@/utils/appLinks";

const GDPR_CHECKS_FLAG = "ui-enable-gdpr-checks";
const FLAG_READY_TIMEOUT_MS = 2000;

export type AppLinkRoutingState = {
  isFra1Region: boolean;
  gdprChecksEnabled: boolean;
  isReady: boolean;
};

const defaultState: AppLinkRoutingState = {
  isFra1Region: false,
  gdprChecksEnabled: false,
  isReady: true,
};

const AppLinkRoutingContext = createContext<AppLinkRoutingState>(defaultState);

export function AppLinkRoutingStateProvider({
  value,
  children,
}: Readonly<{
  value: AppLinkRoutingState;
  children: ReactNode;
}>) {
  return (
    <AppLinkRoutingContext.Provider value={value}>
      {children}
    </AppLinkRoutingContext.Provider>
  );
}

export function AppLinkRoutingProvider({
  isFra1Region,
  children,
}: Readonly<{
  isFra1Region: boolean;
  children: ReactNode;
}>) {
  const posthog = usePostHog();
  const gdprChecksFlag = useFeatureFlag(GDPR_CHECKS_FLAG);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const hasPostHogKey = Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
  const gdprChecksEnabled = gdprChecksFlag?.enabled === true;
  const isFlagResolved = gdprChecksFlag !== undefined;
  const isReady =
    !isFra1Region || !hasPostHogKey || isFlagResolved || hasTimedOut;

  useEffect(() => {
    if (!isFra1Region || !hasPostHogKey) return;
    posthog.reloadFeatureFlags();
  }, [hasPostHogKey, isFra1Region, posthog]);

  useEffect(() => {
    if (!isFra1Region || !hasPostHogKey || isFlagResolved) return;

    const timeoutId = globalThis.setTimeout(() => {
      setHasTimedOut(true);
    }, FLAG_READY_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [hasPostHogKey, isFlagResolved, isFra1Region]);

  return (
    <AppLinkRoutingStateProvider
      value={{ isFra1Region, gdprChecksEnabled, isReady }}
    >
      {children}
    </AppLinkRoutingStateProvider>
  );
}

export function useAppLinkRouting(): AppLinkRoutingState {
  return useContext(AppLinkRoutingContext);
}

export function useRoutedGailAppUrl(href: string): string {
  const { isFra1Region, gdprChecksEnabled } = useAppLinkRouting();
  return routeGailAppUrl(href, { isFra1Region, gdprChecksEnabled });
}
